<form [formGroup]="reservationForm" (ngSubmit)="onSubmit()" class="space-y-4">
  <!-- Habitación -->
  <div class="flex flex-col gap-2">
    <label for="room" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.room' | translate }} *
    </label>
    <p-select
      inputId="room"
      [options]="roomOptions"
      formControlName="room"
      [placeholder]="'reservations.form.selectRoom' | translate"
      appendTo="body"
      [filter]="true"
      [filterPlaceholder]="'common.search' | translate"
      styleClass="w-full"
    >
      <ng-template let-option pTemplate="item">
        {{ option.label }}
      </ng-template>
      <ng-template let-option pTemplate="selectedItem">
        {{ option.label }}
      </ng-template>
    </p-select>
    @if (reservationForm.get('room')?.invalid && reservationForm.get('room')?.touched) {
      <small class="text-red-500">
        {{ 'reservations.form.errors.roomRequired' | translate }}
      </small>
    }
  </div>

  <!-- Búsqueda de Paciente (AutoComplete) -->
  <div class="flex flex-col gap-2">
    <label for="patient" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.patient' | translate }} *
    </label>

    <!-- Modo vista: Solo mostrar el nombre del paciente -->
    @if (isViewMode) {
      <div
        class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <span class="text-base text-gray-900 dark:text-white">
          {{
            selectedPatient
              ? selectedPatient.first_name + ' ' + selectedPatient.last_name
              : ('reservations.form.patientUnavailable' | translate)
          }}
        </span>
        @if (selectedPatient && selectedPatient.email) {
          <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {{ selectedPatient.email }}
          </div>
        }
      </div>
    }

    <!-- Modo edición/creación: Autocomplete -->
    @if (!isViewMode) {
      <p-autoComplete
        inputId="patient"
        [(ngModel)]="selectedPatient"
        [ngModelOptions]="{ standalone: true }"
        [suggestions]="filteredPatients"
        (completeMethod)="searchPatient($event)"
        (onSelect)="onPatientSelect($event)"
        (onClear)="onPatientClear()"
        [placeholder]="'reservations.form.patientSearchPlaceholder' | translate"
        [minLength]="2"
        [dropdown]="false"
        [forceSelection]="true"
        dataKey="patient_id"
        [optionLabel]="getPatientOptionLabel"
        styleClass="w-full"
        inputStyleClass="w-full"
      >
        <ng-template let-patient pTemplate="item">
          <div class="flex flex-col py-2">
            <span class="font-semibold">{{ patient.first_name }} {{ patient.last_name }}</span>
            @if (patient.email) {
              <span class="text-sm text-gray-500">{{ patient.email }}</span>
            }
          </div>
        </ng-template>
      </p-autoComplete>
    }
    @if (reservationForm.get('patient')?.invalid && reservationForm.get('patient')?.touched) {
      <small class="text-red-500">
        {{ 'reservations.form.errors.patientRequired' | translate }}
      </small>
    }
  </div>

  <!-- Programa -->
  <div class="flex flex-col gap-2">
    <label for="program" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.program' | translate }} *
    </label>

    <!-- Modo vista: Mostrar programa seleccionado o mensaje -->
    @if (isViewMode) {
      <div
        class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <span class="text-base text-gray-900 dark:text-white">
          {{ selectedProgram ? selectedProgram.name : ('common.notAvailable' | translate) }}
        </span>
        @if (selectedProgram && selectedProgram.description) {
          <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {{ selectedProgram.description }}
          </div>
        }
      </div>
    }

    <!-- Modo edición/creación: Dropdown -->
    @if (!isViewMode) {
      <p-select
        inputId="program"
        [options]="programOptions"
        formControlName="program"
        (onChange)="onProgramChange($event)"
        [placeholder]="'reservations.form.selectProgram' | translate"
        appendTo="body"
        [filter]="true"
        styleClass="w-full"
      >
        <ng-template let-option pTemplate="item">
          {{ option.label }}
        </ng-template>
        <ng-template let-option pTemplate="selectedItem">
          {{ option.label }}
        </ng-template>
      </p-select>
    }
    @if (
      !isViewMode &&
      reservationForm.get('program')?.invalid &&
      reservationForm.get('program')?.touched
    ) {
      <small class="text-red-500">
        {{ 'reservations.form.errors.programRequired' | translate }}
      </small>
    }
  </div>

  <!-- Plan / Paquete por Días -->
  <div class="flex flex-col gap-2">
    <label for="plan" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.package' | translate }} *
    </label>

    <!-- Modo vista: Mostrar días calculados -->
    @if (isViewMode) {
      <div
        class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <span class="text-base text-gray-900 dark:text-white">
          {{ 'reservations.form.packageDays' | translate: { count: getPackageDaysFromDates() } }}
        </span>
        @if (getTreatmentDay()) {
          <div class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {{ 'reservations.form.treatmentDay' | translate }}: {{ getTreatmentDay() }}
          </div>
        }
      </div>
    }

    <!-- Modo edición/creación: Dropdown -->
    @if (!isViewMode && availablePlans.length > 0) {
      <p-select
        inputId="plan"
        [options]="planOptions"
        formControlName="plan"
        [placeholder]="'reservations.form.selectPlanByDays' | translate"
        appendTo="body"
        styleClass="w-full"
      >
        <ng-template let-option pTemplate="item">
          {{ option.label }}
        </ng-template>
        <ng-template let-option pTemplate="selectedItem">
          {{ option.label }}
        </ng-template>
      </p-select>
    }
    @if (!isViewMode && getTreatmentDay()) {
      <small class="text-gray-600 dark:text-gray-400">
        {{ 'reservations.form.treatmentDay' | translate }}: {{ getTreatmentDay() }}
      </small>
    }
    @if (
      !isViewMode && reservationForm.get('plan')?.invalid && reservationForm.get('plan')?.touched
    ) {
      <small class="text-red-500">
        {{ 'reservations.form.errors.planRequired' | translate }}
      </small>
    }
  </div>

  <!-- Fechas -->
  <div class="grid grid-cols-2 gap-4">
    <div class="flex flex-col gap-2">
      <label for="check_in_date" class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ 'reservations.form.checkInDate' | translate }} *
      </label>
      <p-datepicker
        inputId="check_in_date"
        formControlName="check_in_date"
        [showIcon]="true"
        dateFormat="dd/mm/yy"
        [readonlyInput]="true"
        [minDate]="isEditMode ? null : minDate"
        appendTo="body"
        styleClass="w-full"
      />
      @if (
        reservationForm.get('check_in_date')?.invalid &&
        reservationForm.get('check_in_date')?.touched
      ) {
        <small class="text-red-500">
          {{ 'reservations.form.errors.checkInRequired' | translate }}
        </small>
      }
    </div>

    <div class="flex flex-col gap-2">
      <label for="check_out_date" class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ 'reservations.form.checkOutDate' | translate }}
      </label>
      <p-datepicker
        inputId="check_out_date"
        formControlName="check_out_date"
        [showIcon]="true"
        dateFormat="dd/mm/yy"
        [readonlyInput]="true"
        [disabled]="true"
        appendTo="body"
        styleClass="w-full"
      />
    </div>
  </div>

  <!-- Mensaje de error de disponibilidad -->
  @if (availabilityError) {
    <div
      class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
    >
      <div class="flex items-start gap-3">
        <i class="pi pi-exclamation-triangle text-red-600 dark:text-red-400 text-xl mt-0.5"></i>
        <div class="flex-1">
          <h4 class="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
            {{ 'reservations.form.errors.roomNotAvailableTitle' | translate }}
          </h4>
          <p class="text-sm text-red-700 dark:text-red-400">{{ availabilityError }}</p>
        </div>
      </div>
    </div>
  }

  <!-- Indicador de validación -->
  @if (isValidatingAvailability) {
    <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
      <i class="pi pi-spin pi-spinner"></i>
      <span>{{ 'reservations.form.messages.validatingDetail' | translate }}</span>
    </div>
  }

  <!-- Estado -->
  <div class="flex flex-col gap-2">
    <label for="status" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.status.label' | translate }} *
    </label>
    <p-select
      inputId="status"
      [options]="statusOptions"
      formControlName="status"
      [placeholder]="'reservations.form.selectStatus' | translate"
      appendTo="body"
      styleClass="w-full"
    >
      <ng-template let-option pTemplate="item">
        {{ option.labelKey | translate }}
      </ng-template>
      <ng-template let-option pTemplate="selectedItem">
        {{ option.labelKey | translate }}
      </ng-template>
    </p-select>
  </div>

  <!-- Monto Total -->
  <div class="flex flex-col gap-2">
    <label for="total_amount" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.totalAmount' | translate }} *
    </label>
    <p-inputNumber
      inputId="total_amount"
      formControlName="total_amount"
      mode="currency"
      currency="USD"
      locale="en-US"
      [minFractionDigits]="2"
      [min]="0"
      [disabled]="true"
      styleClass="w-full"
    />
    <small class="text-gray-600 dark:text-gray-400">
      {{ 'reservations.form.totalAmountAutoCalculated' | translate }}
    </small>
  </div>

  <!-- Solicitudes Especiales -->
  <div class="flex flex-col gap-2">
    <label for="special_requests" class="text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ 'reservations.form.specialRequests' | translate }}
    </label>
    <textarea
      id="special_requests"
      pTextarea
      formControlName="special_requests"
      rows="3"
      [placeholder]="'reservations.form.specialRequestsPlaceholder' | translate"
      class="w-full resize-none"
    ></textarea>
  </div>

  <!-- Mensaje informativo en modo edición si no hay cambios -->
  @if (isEditMode && !statusHasChanged && !checkInDateHasChanged) {
    <div
      class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
    >
      <div class="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
        <i class="pi pi-info-circle"></i>
        <span>{{ 'reservations.form.messages.noChangesDetail' | translate }}</span>
      </div>
    </div>
  }

  <!-- Botones (solo en modo edición/creación) -->
  @if (!isViewMode) {
    <div class="flex justify-end gap-3 pt-4">
      <p-button
        type="button"
        (onClick)="onCancel()"
        [label]="'common.cancel' | translate"
        severity="secondary"
        [outlined]="true"
      />
      <p-button
        type="submit"
        [disabled]="
          isEditMode ? !statusHasChanged && !checkInDateHasChanged : reservationForm.invalid
        "
        [icon]="isEditMode ? 'pi pi-save' : 'pi pi-check'"
        [label]="
          (isEditMode ? 'common.save' : 'reservations.actions.confirmReservation') | translate
        "
      />
    </div>
  }

  <!-- Mensaje informativo en modo solo lectura -->
  @if (isViewMode) {
    <div class="flex justify-center pt-4">
      <p class="text-sm text-gray-500 dark:text-gray-400 italic">
        <i class="pi pi-info-circle mr-2"></i>
        {{ 'reservations.form.readOnlyInfo' | translate }}
      </p>
    </div>
  }
</form>
